import { CARD_H, CARD_W } from "./constants.js";
import { resizeArea, toU8 } from "./image.js";
import { grayPlane } from "./signature.js";
import { boxPixels, regionsFor } from "./ocrprep.js";

/* ===========================================================================
   The set-symbol channel — cardvision/index/symbols.py, query side.

   The printed set symbol is matched as a shape, not a tone: the glyph crop is resized
   to 20 x 16, described by its unit-norm Sobel edge magnitude, and scored as the max
   cosine against the candidate's (set, frame, full-art, rarity) templates over a small
   offset grid. Edges make rarity recolouring and showcase polarity flips free; the
   offset grid absorbs the few pixels a camera warp is registered worse than the
   Scryfall scans the templates were built from.
   =========================================================================== */

//: cardvision.index.symbols.SYMBOL_PATCH — descriptor raster, width x height.
export const SYMBOL_PATCH = [20, 16];

//: cardvision.index.symbols.SYMBOL_OFFSETS — canonical-raster pixels.
export const SYMBOL_OFFSETS = (() => {
  const offsets = [];
  for (const dx of [-8, -5, -2, 0, 2, 5, 8])
    for (const dy of [-5, -2, 0, 2, 5]) offsets.push([dx, dy]);
  return offsets;
})();

/**
 * symbols.edge_patch — unit-norm Sobel magnitude of the 20 x 16 crop.
 *
 * The resize goes through uint8 (`toU8`) so both sides differentiate identical
 * integers; the Sobel is 3 x 3 with OpenCV's default reflect-101 border.
 */
export function edgePatch(gray, w, h) {
  if (w <= 0 || h <= 0) return null;
  const [pw, ph] = SYMBOL_PATCH;
  const small = toU8(resizeArea(gray, w, h, pw, ph, 1));
  const magnitude = new Float32Array(pw * ph);
  // reflect-101: index -1 mirrors to 1, index n mirrors to n - 2.
  const rx = (x) => (x < 0 ? -x : x >= pw ? 2 * pw - 2 - x : x);
  const ry = (y) => (y < 0 ? -y : y >= ph ? 2 * ph - 2 - y : y);
  const at = (x, y) => small[ry(y) * pw + rx(x)];
  let mean = 0;
  for (let y = 0; y < ph; y++) {
    for (let x = 0; x < pw; x++) {
      const gx =
        at(x + 1, y - 1) -
        at(x - 1, y - 1) +
        2 * (at(x + 1, y) - at(x - 1, y)) +
        (at(x + 1, y + 1) - at(x - 1, y + 1));
      const gy =
        at(x - 1, y + 1) -
        at(x - 1, y - 1) +
        2 * (at(x, y + 1) - at(x, y - 1)) +
        (at(x + 1, y + 1) - at(x + 1, y - 1));
      const value = Math.sqrt(gx * gx + gy * gy);
      magnitude[y * pw + x] = value;
      mean += value;
    }
  }
  mean /= pw * ph;
  let norm = 0;
  for (let i = 0; i < magnitude.length; i++) {
    magnitude[i] -= mean;
    norm += magnitude[i] * magnitude[i];
  }
  norm = Math.sqrt(norm);
  if (norm < 1e-6) return null;
  for (let i = 0; i < magnitude.length; i++) magnitude[i] /= norm;
  return magnitude;
}

/** symbols.query_patches — every offset's descriptor, computed once per frame preset. */
export function queryPatches(card, frame, fullArt) {
  const [x0, y0, x1, y1] = boxPixels(regionsFor(frame, fullArt).set_symbol);
  const patches = [];
  for (const [dx, dy] of SYMBOL_OFFSETS) {
    const left = Math.max(0, x0 + dx);
    const right = Math.max(0, Math.min(CARD_W, x1 + dx));
    const top = Math.max(0, y0 + dy);
    const bottom = Math.max(0, Math.min(CARD_H, y1 + dy));
    const w = right - left,
      h = bottom - top;
    if (w <= 0 || h <= 0) continue;
    const patch = edgePatch(grayPlane(card, CARD_W, CARD_H, left, top, right, bottom), w, h);
    if (patch) patches.push(patch);
  }
  return patches;
}

/** symbols.best_score — max cosine between any query offset and any reference mode. */
export function bestScore(patches, modes) {
  let best = 0;
  for (const patch of patches) {
    for (const mode of modes) {
      let dot = 0;
      for (let i = 0; i < patch.length; i++) dot += patch[i] * mode[i];
      if (dot > best) best = dot;
    }
  }
  return best;
}

/**
 * Decode a version-2 set_symbols.json into the map `applySetSymbol` reads.
 *
 * Returns null for anything that is not the version-2 shape — including the version-1
 * dHash table older bundles ship, which measured useless and must be refused rather
 * than scored against.
 */
export function decodeSetSymbols(json) {
  if (!json || json.version !== 2 || !json.entries) return null;
  const [pw, ph] = SYMBOL_PATCH;
  const table = new Map();
  for (const [key, entry] of Object.entries(json.entries)) {
    const modes = [];
    for (const encoded of entry.modes || []) {
      const raw = atob(encoded);
      if (raw.length !== pw * ph * 4) continue;
      const bytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
      modes.push(new Float32Array(bytes.buffer));
    }
    if (modes.length) table.set(key, { samples: entry.samples | 0, modes });
  }
  return table.size ? table : null;
}
